import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import QuoteCTA from "@/components/QuoteCTA";
import { Reveal, Kicker, MaskedLine } from "@/components/Reveal";

const PAGES = {
  warranty: {
    title: "Warranty",
    kicker: "Sample Policy — To Be Approved",
    intro: "This is sample warranty wording based on common practice across timber, door and outdoor furniture suppliers. Final warranty terms are to be approved by Mr Kwila Outdoor Furniture Pty Ltd before publication.",
    sections: [
      {
        h: "Outdoor furniture — 5 year structural warranty",
        p: [
          "Kwila outdoor furniture frames are covered by a 5 year return-to-base warranty against defects in materials and workmanship under normal residential outdoor use, from the date of delivery.",
        ],
      },
      {
        h: "Canvas components — 1 year",
        p: [
          "Canvas slings, backs and cushions are guaranteed for 1 year against manufacturing faults. Canvas is UV fade, mildew and rot resistant, but gradual fading in full sun is a normal characteristic, not a defect.",
        ],
      },
      {
        h: "Timber supply and doors",
        p: [
          "Timber products and solid timber doors are supplied to the quoted species, grade and dimensions. Claims for visible defects must be made within 7 days of delivery and before machining, finishing or installation.",
        ],
      },
      {
        h: "What is not covered",
        p: [
          "Natural characteristics of timber — grain and colour variation, knots, gum veins, tannin release and minor surface checking — are not defects.",
          "Movement, cupping or splitting caused by failure to follow the care guide (including oiling intervals), extreme exposure, improper installation, modification, commercial use, or normal wear and tear.",
        ],
      },
      {
        h: "How to make a claim",
        p: [
          "Contact our Archerfield team with your order or quote reference, photos of the issue and a description. Approved warranty claims will be repaired, replaced or refunded at our discretion, consistent with your rights under the Australian Consumer Law.",
        ],
      },
    ],
  },
  "terms-and-conditions": {
    title: "Terms & Conditions",
    kicker: "Sample Policy — To Be Approved",
    intro: "Sample terms for the supply of timber, doors and outdoor furniture. To be reviewed and approved by Mr Kwila Outdoor Furniture Pty Ltd before publication.",
    sections: [
      {
        h: "Quotes and pricing",
        p: [
          "All pricing is provided on request. Quotes are valid for 14 days unless stated otherwise and may be adjusted for specification, volume, freight or exchange-rate changes before acceptance.",
        ],
      },
      {
        h: "Orders and acceptance",
        p: [
          "An order is accepted when confirmed in writing by Mr Kwila. Made-to-order and custom products (including custom doors and processed timber) may require a deposit before manufacture and cannot be cancelled once production has commenced.",
        ],
      },
      {
        h: "Delivery and freight",
        p: [
          "Bulky items are delivered by quoted freight or collected from Archerfield, Brisbane by arrangement. Delivery timeframes are estimates. Customers must advise access restrictions when requesting a freight quote.",
        ],
      },
      {
        h: "Natural timber variation",
        p: [
          "Timber is a natural product. Variation in grain, tone, colour and natural features is expected and forms part of the character of the product.",
        ],
      },
      {
        h: "Risk, title and liability",
        p: [
          "Risk passes on delivery or collection; title passes on full payment. Nothing in these terms limits rights that cannot be excluded under the Australian Consumer Law. These terms are governed by the laws of Queensland, Australia.",
        ],
      },
    ],
  },
  "privacy-policy": {
    title: "Privacy Policy",
    kicker: "Sample Policy — To Be Approved",
    intro: "Sample privacy wording for an Australian ecommerce and trade enquiry website. To be reviewed and approved before publication.",
    sections: [
      {
        h: "What we collect",
        p: [
          "Details you submit through quote requests, trade applications, contact forms and newsletter signup — typically name, business name, email, phone, delivery location and project details. A session cookie keeps you logged in to this site for 24 hours.",
        ],
      },
      {
        h: "How we use it",
        p: [
          "To respond to enquiries, prepare quotes, process trade applications, arrange delivery and (with your consent) send occasional product news. We do not sell your information.",
        ],
      },
      {
        h: "Storage and disclosure",
        p: [
          "Enquiry data is stored securely in our systems and shared only with delivery or manufacturing partners where needed to fulfil your request, or where required by law.",
        ],
      },
      {
        h: "Access and questions",
        p: [
          "You may request access to, or correction of, your personal information at any time by contacting our team. This policy may be updated as the site evolves.",
        ],
      },
    ],
  },
  "delivery-information": {
    title: "Delivery Information",
    kicker: "Sample Policy — To Be Approved",
    intro: "Sample delivery wording for bulky timber, door and furniture orders. Final delivery zones, rates and timeframes are to be confirmed by Mr Kwila Outdoor Furniture Pty Ltd before publication.",
    sections: [
      {
        h: "How delivery works",
        p: [
          "Most of our products are bulky and travel by quoted freight rather than standard courier. Add items to your quote list, include your delivery postcode and any access notes, and our Archerfield team will confirm a delivery price with your quote.",
          "Local pickup from our Archerfield, Brisbane warehouse is available by arrangement.",
        ],
      },
      {
        h: "Delivery areas and timeframes",
        p: [
          "We deliver across Brisbane, Queensland and Australia-wide. Timeframes depend on stock, manufacture lead times and freight scheduling — confirmed with every quote. [Delivery zones and standard timeframes to be confirmed by client.]",
        ],
      },
      {
        h: "Access and placement",
        p: [
          "Please tell us about stairs, steep driveways, narrow access or overhead obstacles when you order. Standard delivery is to ground-floor or driveway unless otherwise arranged.",
        ],
      },
      {
        h: "Receiving your order",
        p: [
          "Check items on arrival and note any visible damage on the delivery docket before signing. Report transit damage to our team within 48 hours with photos so we can resolve it quickly.",
        ],
      },
      {
        h: "Before you position Kwila",
        p: [
          "New Kwila releases natural tannins when wet. Hose down and allow it to dry twice on grass or gravel before placing furniture or timber on paving, tiles or concrete — see our care guide.",
        ],
      },
    ],
  },
  returns: {
    title: "Returns",
    kicker: "Sample Policy — To Be Approved",
    intro: "Sample returns wording for a quote-based timber, doors and furniture business. To be reviewed and approved by Mr Kwila Outdoor Furniture Pty Ltd before publication.",
    sections: [
      {
        h: "Faulty or damaged goods",
        p: [
          "If an item arrives damaged or with a manufacturing fault, contact us within 48 hours of delivery with photos and your order reference. We will repair, replace or refund in line with your rights under the Australian Consumer Law.",
        ],
      },
      {
        h: "Change of mind",
        p: [
          "As products are bulky and often made or prepared to order, change-of-mind returns are assessed case by case and may be declined or subject to a restocking fee and return freight costs. [Final change-of-mind policy to be confirmed by client.]",
        ],
      },
      {
        h: "Custom and made-to-order items",
        p: [
          "Custom doors, processed timber and cut-to-size products cannot be returned unless faulty, as they are manufactured to your specifications.",
        ],
      },
      {
        h: "Natural timber characteristics",
        p: [
          "Variation in grain, tone and colour, tannin release and natural features such as knots and gum veins are characteristics of natural timber, not faults, and are not grounds for return.",
        ],
      },
      {
        h: "How to start a return",
        p: [
          "Contact our Archerfield team with your order or quote reference and photos. Do not return items without an approved return authorisation, as return freight must be arranged for bulky goods.",
        ],
      },
    ],
  },
  "timber-sourcing": {
    title: "Timber Sourcing",
    kicker: "Sample Statement — To Be Approved",
    intro: "Mr Kwila is committed to responsible timber sourcing. This is a placeholder statement — final certification, legality and chain-of-custody documentation is to be supplied by the client and added before launch.",
    sections: [
      {
        h: "Our supply chain",
        p: [
          "Mr Kwila timber is manufactured in Papua New Guinea by our linked PNG group, which operates sawmilling, kiln drying, processing and joinery facilities, and is warehoused and distributed from Archerfield, Brisbane.",
        ],
      },
      {
        h: "Responsible sourcing commitment",
        p: [
          "The Kwila used in the outdoor furniture range is sourced from the managed forest areas of Papua New Guinea. [Sourcing documentation to be supplied by client.]",
        ],
      },
      {
        h: "Certification and legality",
        p: [
          "Any certification, legality or chain-of-custody claims will be published here once supporting documentation is supplied. We do not make FSC, PEFC or legal-harvest claims without documentation.",
        ],
      },
      {
        h: "Questions",
        p: [
          "Trade and project buyers seeking provenance documentation for compliance purposes should contact our team with their requirements.",
        ],
      },
    ],
  },
  accessibility: {
    title: "Accessibility",
    kicker: "Sample Statement — To Be Approved",
    intro: "Mr Kwila aims to make this website usable by as many people as possible. This is a sample accessibility statement to be reviewed before publication.",
    sections: [
      {
        h: "Our commitment",
        p: [
          "We aim to meet WCAG 2.1 Level AA where practical, and to keep the site clear, high-contrast and navigable by keyboard and screen reader.",
        ],
      },
      {
        h: "Measures in place",
        p: [
          "Semantic page structure and headings, keyboard-accessible menus and forms, text alternatives on meaningful images, and colour contrast aligned to the brand palette.",
        ],
      },
      {
        h: "Known limitations",
        p: [
          "Some placeholder imagery and the downloadable PDF brochures may not yet be fully accessible. We are working on improving these as final content is supplied.",
        ],
      },
      {
        h: "Feedback",
        p: [
          "If you have difficulty using any part of this site, contact our team and we will provide the information in an alternative format.",
        ],
      },
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const page = PAGES[slug];

  if (!page)
    return (
      <div className="px-5 pb-32 pt-48 text-center" data-testid="legal-not-found">
        <p className="font-display text-4xl">Page not found.</p>
        <Link to="/" className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-kwila">
          <ArrowLeft size={15} /> Back to home
        </Link>
      </div>
    );

  return (
    <div data-testid={`legal-page-${slug}`}>
      <section className="px-5 pb-12 pt-32 md:px-10 md:pt-44">
        <div className="mx-auto max-w-[900px]">
          <Kicker>{page.kicker}</Kicker>
          <h1 className="mt-4 font-display text-5xl font-light tracking-tight md:text-6xl">
            <MaskedLine delay={0.1}>{page.title}</MaskedLine>
          </h1>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-ink/60">{page.intro}</p>
        </div>
      </section>
      <section className="px-5 pb-24 md:px-10">
        <div className="mx-auto max-w-[900px]">
          {page.sections.map((s, i) => (
            <Reveal key={s.h} className="border-t border-ink/10 py-8">
              <div className="grid gap-4 md:grid-cols-12">
                <div className="md:col-span-4">
                  <span className="text-xs font-bold text-kwila">0{i + 1}</span>
                  <h2 className="mt-1 font-display text-2xl tracking-tight">{s.h}</h2>
                </div>
                <div className="space-y-3 text-sm leading-relaxed text-ink/70 md:col-span-8">
                  {s.p.map((para) => (
                    <p key={para.slice(0, 24)}>{para}</p>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <QuoteCTA />
    </div>
  );
}
